<template>
  <div class="floating-node-panel" :class="{ 'collapsed': panelCollapsed }">
    <v-card elevation="8" class="panel-card">
      <v-card-title class="panel-header">
        <div class="header-content">
          <v-icon class="header-icon">mdi-view-grid-plus</v-icon>
          <span v-if="!panelCollapsed" class="panel-title">Nodes</span>
        </div>
      </v-card-title>

      <v-card-text v-if="!panelCollapsed" class="panel-content">
        <!-- Search -->
        <div class="search-container">
          <v-text-field v-model="searchTerm" placeholder="Search nodes..." density="compact" variant="outlined"
            hide-details prepend-inner-icon="mdi-magnify" clearable />
        </div>

        <!-- Node Categories -->
        <div class="categories">
          <v-expansion-panels v-model="openPanels" multiple variant="accordion" class="category-panels">
            <v-expansion-panel v-for="(category, index) in filteredCategories" :key="category.name" :value="index">
              <v-expansion-panel-title class="category-header">
                <div class="category-title-content">
                  <v-icon size="16" class="category-icon">{{ category.icon }}</v-icon>
                  <span class="category-title">{{ category.name }}</span>
                  <v-chip size="x-small" class="node-count">{{ category.nodes.length }}</v-chip>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text class="category-content">
                <div class="category-nodes">
                  <div v-for="node in category.nodes" :key="node.type" class="draggable-node"
                    :class="`node-${node.type}`" draggable="true" @dragstart="onDragStart($event, node)"
                    @click="addNode(node)">
                    <div class="node-preview">
                      <div class="node-icon" :style="{ backgroundColor: node.iconBg, color: node.iconColor }">
                        <v-icon size="16">{{ node.icon }}</v-icon>
                      </div>
                      <div class="node-info">
                        <div class="node-name">{{ node.name }}</div>
                        <div class="node-description">{{ node.description }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <!-- Collapsed state -->
      <v-card-text v-else class="collapsed-content">
        <div class="quick-nodes">
          <v-tooltip v-for="node in quickNodes" :key="node.type" :text="node.name" location="right">
            <template v-slot:activator="{ props }">
              <div v-bind="props" class="quick-node" draggable="true" @dragstart="onDragStart($event, node)"
                @click="addNode(node)">
                <v-icon size="18" :color="node.iconColor">{{ node.icon }}</v-icon>
              </div>
            </template>
          </v-tooltip>
        </div>
      </v-card-text>
      <v-card-action style="display: flex; justify-content: flex-end; align-items: center;"
        :style="panelCollapsed ? 'justify-content: center;padding-bottom: 5px;' : 'justify-content: flex-end;'">
        <v-btn icon size="small"  @click="togglePanel" class="toggle-btn collapse-btn"
          :class="{ 'collapsed-hint animated-hint-btn': panelCollapsed }">
          <div class="btn-content">
            <!-- Hint animation elements -->
            <!-- <div v-if="panelCollapsed" class="hint-circle hint-circle-1"></div>
            <div v-if="panelCollapsed" class="hint-circle hint-circle-2"></div>
            <div v-if="panelCollapsed" class="hint-circle hint-circle-3"></div> -->

            <!-- Original content -->
            <div class="original-content">
              <transition name="collapse-text" mode="out-in">
                <span v-if="!panelCollapsed" class="collapse-text">Collapse</span>
              </transition>
              <v-icon class="toggle-icon" :style="{ color: panelCollapsed ? 'white' : 'var(--v-theme-primary)' }">
                {{ panelCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
              </v-icon>
            </div>
          </div>
        </v-btn>
      </v-card-action>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'NodePanel',
  emits: ['add-node'],
  props: {
    // Filter out specific node types (e.g., ['messageInput', 'response', 'orchestrator'])
    excludeTypes: {
      type: Array,
      default: () => []
    },
    // Compact mode for embedding in dialogs
    compactMode: {
      type: Boolean,
      default: false
    },
    // Start expanded
    startExpanded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      panelCollapsed: !this.startExpanded,
      searchTerm: '',
      openPanels: [0, 1, 2, 3, 4, 5],
      nodeCategories: [
        {
          name: 'Input/Output',
          icon: 'mdi-import',
          nodes: [
            {
              type: 'messageInput',
              name: 'Message Input',
              description: 'User text input',
              icon: 'mdi-chat',
              iconBg: '#ecfdf5',
              iconColor: '#10b981',
              data: { label: 'Message Input', type: 'messageInput' }
            },
            // {
            //   type: 'api',
            //   name: 'API Call',
            //   description: 'External API request',
            //   icon: 'mdi-api',
            //   iconBg: '#fffbeb',
            //   iconColor: '#f59e0b',
            //   data: { label: 'API Call', type: 'api' }
            // },
            {
              type: 'response',
              name: 'Response',
              description: 'Bot response output',
              icon: 'mdi-message-reply-text',
              iconBg: '#ffeded',
              iconColor: '#ef4444 ',
              data: { label: 'Response', type: 'response' }
            },
            // {
            //   type: 'textOutput',
            //   name: 'Text Output',
            //   description: 'Display text result',
            //   icon: 'mdi-text-box',
            //   iconBg: '#e0f2fe',
            //   iconColor: '#0288d1',
            //   data: { label: 'Text Output', type: 'textOutput' }
            // }
          ]
        },
        {
          name: 'AI',
          icon: 'mdi-brain',
          nodes: [
            {
              type: 'model',
              name: 'AI Model',
              description: 'Language model processor',
              icon: 'mdi-robot',
              iconBg: '#eff6ff',
              iconColor: '#2c6dee',
              data: { label: 'AI Model', type: 'model' }
            },
            {
              type: 'taxPlanning',
              name: 'Tax Planning Form',
              description: 'ถามคำถามภาษีจาก active config แล้วเก็บคำตอบเป็น Tax Form',
              icon: 'mdi-clipboard-text-clock',
              iconBg: '#ecfdf5',
              iconColor: '#10b981',
              data: { label: 'Tax Planning Form', type: 'taxPlanning' }
            },
            {
              type: 'taxPlanningV2',
              name: 'Tax Planning Form V2',
              description: 'ถามคำถามภาษีจาก active config V2 แล้วเก็บคำตอบเป็น Tax Form V2',
              icon: 'mdi-clipboard-text-clock',
              iconBg: '#ecfeff',
              iconColor: '#0891b2',
              data: { label: 'Tax Planning Form V2', type: 'taxPlanningV2' }
            },
            // {
            //   type: 'guardrailAI',
            //   name: 'Guard Rail Model',
            //   description: 'Content safety & validation',
            //   icon: 'mdi-shield-check',
            //   iconBg: '#f0fdf4',
            //   iconColor: '#16a34a',
            //   data: { label: 'Guard Rail Model', type: 'guardrailAI' }
            // },
            {
              type: 'intentAI',
              name: 'Intent AI Model',
              description: 'AI-powered intent detection',
              icon: 'mdi-target-variant',
              iconBg: '#f3eeff',
              iconColor: '#8b5cf6',
              data: { label: 'Intent AI Model', type: 'intentAI' }
            },
            {
              type: 'aiVoice',
              name: 'AI Voice',
              description: 'TTS Processing (SSML Reformat + Break Time)',
              icon: 'mdi-text-to-speech',
              iconBg: '#f3e5f5',
              iconColor: '#9C27B0',
              data: {
                label: 'AI Voice',
                type: 'aiVoice',
                sourceTypeCheck: true,
                bypassTTS: false,
                reformatPrompt: '',
                ssmlPrompt: '',
                model: 'gpt-5-chat',
                temperature: 0.3,
                maxTokens: 4000
              }
            },
            {
              type: 'reformatTTS',
              name: 'Reformat TTS',
              description: 'Step 1: Reformat text for TTS (conversational)',
              icon: 'mdi-text-account',
              iconBg: '#e8f5e9',
              iconColor: '#4caf50',
              data: {
                label: 'Reformat TTS',
                type: 'reformatTTS',
                sourceTypeCheck: true,
                bypassTTS: false,
                reformatPrompt: '',
                model: 'gpt-4o',
                temperature: 0.3,
                maxTokens: 4000
              }
            },
            {
              type: 'ssmlFormat',
              name: 'SSML Format',
              description: 'Step 2: Add SSML break tags for TTS',
              icon: 'mdi-xml',
              iconBg: '#fff3e0',
              iconColor: '#ff9800',
              data: {
                label: 'SSML Format',
                type: 'ssmlFormat',
                ssmlPrompt: '',
                model: 'gpt-4o',
                temperature: 0.3,
                maxTokens: 4000,
                enableStreaming: true
              }
            },
            {
              type: 'queryRewriter',
              name: 'Query Rewriter',
              description: 'Merge context or summarize history with user message',
              icon: 'mdi-message-text-clock-outline',
              iconBg: '#f3e8ff',
              iconColor: '#7c3aed',
              data: {
                label: 'Query Rewriter',
                type: 'queryRewriter',
                // Mode: 'continueQuestion' or 'historySummary'
                queryRewriterMode: 'continueQuestion',
                // Basic AI Configuration
                queryRewriterProvider: null,
                queryRewriterModel: null,
                queryRewriterApiVersion: null,
                // AI Parameters
                queryRewriterTemperature: 0,
                queryRewriterMaxTokens: 500,
                queryRewriterTopP: 1,
                queryRewriterFrequencyPenalty: null,
                queryRewriterPresencePenalty: null,
                // System Prompt
                queryRewriterSystemPrompt: '',
                // Memory Settings (for historySummary mode)
                queryRewriterMemoryTurn: 5,
                queryRewriterMemoryDuration: 60,
                // Response Options
                queryRewriterStreamResponse: false,
                queryRewriterJsonResponse: false
              }
            },
            {
              type: 'llmEnrich',
              name: 'LLM Enrich',
              description: 'ส่ง input ไป LLM แล้วต่อท้ายผลลัพธ์เข้ากับ input เดิม (ใส่ข้อความเชื่อมได้)',
              icon: 'mdi-text-box-plus',
              iconBg: '#f0fdfa',
              iconColor: '#0d9488',
              data: { label: 'LLM Enrich', type: 'llmEnrich' }
            },
          ]
        },
        {
          name: 'Intent',
          icon: 'mdi-target',
          nodes: [
            {
              type: 'intent',
              name: 'Intent Router',
              description: 'Handle specific intent',
              icon: 'mdi-crosshairs-question',
              iconBg: '#f3eeff',
              iconColor: '#8b5cf6',
              data: { label: 'Intent Router', type: 'intent', intentName: 'New Intent' }
            },
            {
              type: 'smartIntent',
              name: 'Smart Intent',
              description: 'AI-enhanced intent handler with individual AI config',
              icon: 'mdi-robot-confused',
              iconBg: '#f0f4ff',
              iconColor: '#4f46e5',
              data: { label: 'Smart Intent', type: 'smartIntent', smartIntentName: 'New Smart Intent' }
            },
            {
              type: 'router',
              name: 'Router',
              description: 'Single LLM call intent routing with JSON response',
              icon: 'mdi-router-wireless',
              iconBg: '#e8f5e9',
              iconColor: '#4caf50',
              data: { label: 'Router', type: 'router', routerName: 'New Router' }
            },
            {
              type: 'advancedRouter',
              name: 'Advanced Router',
              description: 'Hierarchical multi-level intent routing',
              icon: 'mdi-source-branch',
              iconBg: '#f3e8ff',
              iconColor: '#7c3aed',
              data: {
                label: 'Advanced Router',
                type: 'advancedRouter',
                advancedRouterName: 'New Advanced Router',
                customSchemaEnabled: false,
                customSchemaTemplate: {
                  type: 'OBJECT',
                  properties: {
                    intents: {
                      type: 'ARRAY',
                      items: {
                        type: 'OBJECT',
                        properties: {
                          capability_intent: { type: 'STRING' },
                          confidence: { type: 'NUMBER' },
                          intents: { type: 'ARRAY', items: {} }
                        },
                        required: ['capability_intent', 'confidence', 'intents']
                      }
                    }
                  },
                  required: ['intents']
                },
                schemaFieldMapping: {
                  intentField: 'capability_intent',
                  confidenceField: 'confidence',
                  subIntentsField: 'intents'
                }
              }
            },
            {
              type: 'dynamicRouter',
              name: 'Dynamic Router',
              description: 'MetaDocument-aware hierarchical routing with tag resolution',
              icon: 'mdi-file-document-multiple',
              iconBg: '#ccfbf1',
              iconColor: '#0d9488',
              data: {
                label: 'Dynamic Router',
                type: 'dynamicRouter',
                dynamicRouterName: 'New Dynamic Router',
                dynamicRouterOutputs: [],
                dynamicRouterConfidenceThreshold: 0.5,
                dynamicRouterMaxDepth: 2
              }
            },
            {
              type: 'apiTrigger',
              name: 'API Trigger',
              description: 'Trigger external API action',
              icon: 'mdi-rocket-launch',
              iconBg: '#f0f9ff',
              iconColor: '#0ea5e9',
              data: { label: 'API Trigger', type: 'apiTrigger' }
            },
            {
              type: 'apiRequest',
              name: 'API Request',
              description: 'Call external API and use response data in next nodes',
              icon: 'mdi-api',
              iconBg: '#ede9fe',
              iconColor: '#6366f1',
              data: { label: 'API Request', type: 'apiRequest' }
            },
            {
              type: 'favoriteMatcher',
              name: 'Favorite Matcher',
              description: 'Match favorites from API response (TRANSFER/TOPUP/BILL_PAYMENT). Attach under API Request.',
              icon: 'mdi-account-search',
              iconBg: '#f0fdfa',
              iconColor: '#0d9488',
              data: { label: 'Favorite Matcher', type: 'favoriteMatcher', serviceType: 'TRANSFER', favoritesSourceVariable: 'apiRequestResponse', outputVariable: 'userMessage', enableEmbedding: true, fuzzyThreshold: 0.85, embeddingThreshold: 0.65, maxResults: 0 }
            },
            {
              type: 'serviceMatcher',
              name: 'Service Matcher',
              description: 'Match user text against DB data (banks/operators/billers). Outputs matches[] for Service Fallback.',
              icon: 'mdi-database-search',
              iconBg: '#ecfeff',
              iconColor: '#0891b2',
              // NOTE: keep in sync with Flow.vue -> applyNodeDefaults / openNodeInspector /
              // extractNodeData(case 'serviceMatcher') / expandNodeData(case 'serviceMatcher').
              // favoriteConditions/serviceConditions stay empty here on purpose: applyNodeDefaults
              // seeds the default `match_count > 0` rule with a freshly generated unique id.
              // apiRequestHeaders is deliberately NOT listed: addNode() shallow-copies this
              // object, so a literal array here would be shared by every node created from the
              // palette. applyNodeDefaults builds a fresh [{key,value}] row per node instead.
              data: {
                label: 'Service Matcher', type: 'serviceMatcher',
                serviceType: 'TRANSFER', dataIntegrationId: null,
                inputVariable: 'userMessage', outputVariable: 'userMessage',
                activeOnly: true, enableEmbedding: false, fuzzyThreshold: 0.85, embeddingThreshold: 0.65, maxResults: 0,
                // Unified Favorite Lookup (OFF = legacy single-output behaviour)
                enableFavoriteLookup: false, matchStrategy: 'favorite-first',
                // API Request parity
                apiRequestUrl: '', apiRequestMethod: 'GET', apiRequestBody: '',
                apiRequestResponsePath: '', timeout: 30, useLHBService: false,
                // Favorite matching
                favoritesSourceVariable: '', favoriteEnableEmbedding: true,
                favoriteKeywordFunnel: true,
                favoriteFuzzyThreshold: 0.85, favoriteEmbeddingThreshold: 0.65, favoriteMaxResults: 0,
                // Branch decision rules
                favoriteConditions: [], favoriteMatchMode: 'all',
                serviceConditions: [], serviceMatchMode: 'all',
                // Output handle labels
                favoriteLabel: 'Favorite', serviceLabel: 'Service', noMatchLabel: 'No Match'
              }
            },
            {
              type: 'contactAgent',
              name: 'Contact Agent',
              description: 'Transfer to human agent',
              icon: 'mdi-account-supervisor',
              iconBg: '#fef2f2',
              iconColor: '#dc2626',
              data: { label: 'Contact Agent', type: 'contactAgent' }
            },
            {
              type: 'contactAgentV2',
              name: 'Contact Agent V2',
              description: 'Transfer to human agent · ใช้เป็น agent ใต้ Orchestrator ได้ (ต่อที่ handle บน)',
              icon: 'mdi-account-supervisor',
              iconBg: '#fef2f2',
              iconColor: '#dc2626',
              data: { label: 'Contact Agent V2', type: 'contactAgentV2' }
            },
            {
              type: 'interestOptimizer',
              name: 'Interest Optimizer',
              description: 'Optimize interest based on user profile',
              icon: 'mdi-calculator',
              iconBg: '#fed7aa',
              iconColor: '#f97316',
              data: { label: 'Interest Optimizer', type: 'interestOptimizer' }
            },
            {
              type: 'interestOptimizerV2',
              name: 'Interest Optimizer V2',
              description: 'MetaDoc-based interest optimization',
              icon: 'mdi-calculator',
              iconBg: '#fed7aa',
              iconColor: '#f97316',
              data: { label: 'Interest Optimizer V2', type: 'interestOptimizerV2' }
            },
            {
              type: 'loanInterest',
              name: 'Loan Interest',
              description: 'Loan interest rate optimization',
              icon: 'mdi-home-analytics',
              iconBg: '#d1fae5',
              iconColor: '#10b981',
              data: { label: 'Loan Interest', type: 'loanInterest' }
            },
            {
              type: 'loanInterestV2',
              name: 'Loan Interest V2',
              description: 'MetaDoc-based loan interest calculation',
              icon: 'mdi-home-analytics',
              iconBg: '#d1fae5',
              iconColor: '#10b981',
              data: { label: 'Loan Interest V2', type: 'loanInterestV2' }
            },
            {
              type: 'jsonCondition',
              name: 'JSON Condition',
              description: 'Rule-based JSON routing (no AI cost)',
              icon: 'mdi-code-json',
              iconBg: '#ccfbf1',
              iconColor: '#0d9488',
              data: { label: 'JSON Condition', type: 'jsonCondition', jsonConditionName: 'New JSON Condition', matchMode: 'all', conditions: [] }
            },
            {
              type: 'serviceFallback',
              name: 'Service Fallback',
              description: 'Fixed JSON fallback response with language-dependent message',
              icon: 'mdi-redo-variant',
              iconBg: '#fffbeb',
              iconColor: '#f59e0b',
              data: { label: 'Service Fallback', type: 'serviceFallback', serviceType: 'TRANSFER', primaryMessageTH: '***กรุณาเลือกรายการที่ต้องการทำธุรกรรม และตรวจสอบข้อมูลก่อนทำรายการนะคะ', primaryMessageEN: '***Please select the transaction and review the details before confirming.', primaryMessageCN: '***請選擇欲進行交易的項目，並於操作前確認資料是否正確。', primarySingleMessageTH: '***กรุณาตรวจสอบข้อมูลธุรกรรมก่อนทำรายการนะคะ Genie กำลังพาไปหน้าทำรายการ', primarySingleMessageEN: '***Please review the details before confirming the transaction. I am taking you to the transaction screen.', primarySingleMessageCN: '***請在確認交易前先檢查詳細資料 我正在帶您前往交易畫面。', primaryAllowEditOutput: false, primaryOutputJson: '{\n  "message": "{{auto_message}}",\n  "action": "callback",\n  "navlist": [\n    {\n      "serviceType": "TRANSFER",\n      "toType": "{{to_type}}",\n      "toValue": "{{favorite_id}}",\n      "accountNumber": "{{account_no}}",\n      "amount": "{{amount}}",\n      "thubmnail": "{{thumbnail}}",\n      "favorite_name": "{{favorite_name}}"\n    }\n  ]\n}', messageTH: 'ไม่พบรายชื่อนี้ในระบบ Genie กำลังพาไปหน้าทำรายการ กรุณาเลือกทำรายการด้วยตนเอง', messageEN: "I can't find this name in the system. I'm taking you to the transaction screen—please select the transaction manually.", messageCN: '系統中找不到此名稱。我正在帶您前往交易畫面，請您自行選擇交易項目。', allowEditOutput: false, outputJson: '{\n  "message": "{{auto_message}}",\n  "action": "callback",\n  "navlist": [\n    {\n      "serviceType": "TRANSFER",\n      "toType": "N",\n      "toValue": null,\n      "amount": "{{amount}}"\n    }\n  ]\n}' }
            }
          ]
        },
        {
          name: 'Agent Architect',
          icon: 'mdi-robot-industrial',
          nodes: [
            {
              type: 'orchestrator',
              name: 'Orchestrator',
              description: 'Multi-agent coordinator with intent routing',
              icon: 'mdi-hub',
              iconBg: '#fdf2f8',
              iconColor: '#ec4899',
              data: { 
                label: 'Orchestrator', 
                type: 'orchestrator', 
                orchestratorName: 'New Orchestrator',
                confidenceThreshold: 0.7,
                intents: [],
                fallbackAgentId: null
              }
            },
            {
              type: 'agent',
              name: 'Agent',
              description: 'Autonomous agent with tools',
              icon: 'mdi-robot',
              iconBg: '#f0fdf4',
              iconColor: '#22c55e',
              data: {
                label: 'Agent',
                type: 'agent',
                agentName: 'New Agent',
                personaPrompt: '',
                // Final-summary prompt — applyNodeDefaults seeds the built-in text; blank = built-in on the backend
                agentFinalSummaryTemplate: '',
                selectedTools: [],
                maxIterations: 5,
                temperature: 0.7,
                // Hosted tools (การ์ด Tools) — เก็บเป็น JSON string ตัวเดียว, null = ไม่ใช้ tool
                toolsConfig: null
              }
            },
            {
              type: 'taxPlanningAgent',
              name: 'Tax Planning Agent',
              description: 'แบบสอบถามวางแผนภาษี (Tax Planning V2) ในรูปแบบ Agent',
              icon: 'mdi-clipboard-text-clock',
              iconBg: '#eef2ff',
              iconColor: '#6366f1',
              // The two keys the Orchestrator reads to pick an agent, plus its one behaviour switch.
              // Everything else — model, prompts, memory, quick reply — comes from Tax Planning Settings V2,
              // always from whichever version is ACTIVE (no pin: see TaxPlanningAgentNodeProcessor.BuildRequest).
              data: {
                label: 'Tax Planning Agent',
                type: 'taxPlanningAgent',
                agentName: 'วางแผนภาษี',
                agentDescription: 'ช่วยลูกค้ากรอกข้อมูลเพื่อวางแผนภาษีเงินได้บุคคลธรรมดา — รายได้ ค่าลดหย่อน '
                  + 'การคำนวณภาษี และคำแนะนำการวางแผน รวมถึงการตอบคำถามที่ค้างอยู่ในแบบฟอร์มภาษี',
                bypassGuardrailWhenAlone: true,
                taxKnowledgeEnabled: false,
                taxKnowledgeSelections: []
              }
            },
            {
              type: 'buttonStyle',
              name: 'Button Style',
              description: 'Plugin ใต้ Agent: แปลง |productbutton| / |servicebutton| ในคำตอบของ agent ให้เป็นปุ่มพร้อมสไตล์ (JSON) — ค่าในวงเล็บเดิมไปอยู่ที่ value ข้อความอื่นไม่ถูกแตะ',
              icon: 'mdi-gesture-tap-button',
              iconBg: '#fdf4ff',
              iconColor: '#c026d3',
              data: { label: 'Button Style', type: 'buttonStyle' }
            },
            {
              type: 'tool',
              name: 'Tool',
              description: 'Agent tool capability',
              icon: 'mdi-wrench',
              iconBg: '#fffbeb',
              iconColor: '#f59e0b',
              data: { 
                label: 'Tool', 
                type: 'tool', 
                toolName: 'New Tool',
                toolDescription: '',
                toolType: 'master',
                masterToolId: null,
                endpoint: '',
                method: 'GET',
                headers: [],
                body: ''
              }
            },
            {
              type: 'flowTool',
              name: 'Flow Tool',
              description: 'Custom tool with internal flow',
              icon: 'mdi-sitemap',
              iconBg: '#e0e7ff',
              iconColor: '#4f46e5',
              data: { 
                label: 'Flow Tool', 
                type: 'flowTool', 
                flowToolName: 'New Flow Tool',
                flowToolDescription: '',
                internalNodes: [],
                internalEdges: [],
                showWarning: true
              }
            }
          ]
        },
        {
          name: 'Knowledge',
          icon: 'mdi-book-open-variant',
          nodes: [
            {
              type: 'documents',
              name: 'Documents',
              description: 'Document management and search',
              icon: 'mdi-file-document-multiple',
              iconBg: '#f0f9ff',
              iconColor: '#0369a1',
              data: { label: 'Documents', type: 'documents' }
            },
            {
              type: 'dynamicKnowledge',
              name: 'Dynamic Knowledge',
              description: 'Auto-resolves documents from Dynamic Router tags',
              icon: 'mdi-file-document-check',
              iconBg: '#ccfbf1',
              iconColor: '#0d9488',
              data: { label: 'Dynamic Knowledge', type: 'dynamicKnowledge' }
            },
            {
              type: 'databaseIntegration',
              name: 'Database Integration',
              description: 'Connect to knowledge database',
              icon: 'mdi-database-search',
              iconBg: '#f0fdf4',
              iconColor: '#16a34a',
              data: { label: 'Database Integration', type: 'databaseIntegration' }
            },
            {
              type: 'mutualFund',
              name: 'Mutual Fund',
              description: 'Look up bank and Morningstar fund information',
              icon: 'mdi-chart-donut-variant',
              iconBg: '#f5f3ff',
              iconColor: '#7c3aed',
                data: {
                  label: 'Mutual Fund',
                  type: 'mutualFund',
                  mutualFundLookupType: 'fundCode',
                  mutualFundLookupValue: '',
                  mutualFundComparisonValues: '',
                  mutualFundFactsheetConfigCode: '',
                  mutualFundComparisonConfigCode: '',
                  mutualFundPerformanceConfigCode: '',
                  // ชุดการ์ดตามช่วง SDK ของแอป — ว่าง = ทุกแอปใช้ 3 ใบด้านบน
                  mutualFundCardSets: [],
                  // The picker cards (type14) — the ones the customer chooses a fund FROM, drawn only when
                  // the search came back with more than one fund. TWO of them, because the choice differs:
                  // one code per mode, since a stored config carries its own `cp`, wording and join.
                  // Blank = that turn answers with text and no buttons.
                  mutualFundPickerConfigCode: '',
                  mutualFundPickerCompareConfigCode: '',
                  mutualFundDemoMode: false,
                  // Fund resolver — a NEW node resolves the fund from what the customer wrote.
                  // (A node that predates this key keeps the old behaviour: applyNodeDefaults
                  //  seeds it `false` so an existing flow never changes on its own.)
                  mutualFundResolverEnabled: true,
                  // How many funds the node pulls out of the DB in one turn (the whole-turn ceiling).
                  mutualFundTopN: 10,
                  // How many funds go into the comparison card when the customer asks to compare.
                  // Narrower than the Top N above — it caps that one card, not the search.
                  mutualFundMaxSelect: 3,
                  // #924 fuzzy search — finds SCB-A when speech-to-text wrote "SCBA". Off on a new node;
                  // the operator switches it on in the node panel.
                  mutualFundFuzzyEnabled: false,
                  mutualFundFuzzyMinSimilarity: 0.78,
                  // Keep the funds scoring within this of the best one (0.10 = 10 %); an exact 1.00 keeps only exact.
                  mutualFundFuzzyTopGap: 0.1,
                  // LLM settings — shared keys (no prefix) so changeProvider()/apiVersion auto-fill work
                  provider: 'Azure',
                  model: null,
                  apiVersion: null,
                  temperature: 0,
                  topP: 1,
                  maxTokens: null,
                  // Prompts are left blank here on purpose — applyNodeDefaults seeds the built-in
                  // defaults, so a node pasted or imported from an older flow gets them too.
                  // (The retired shortlist keys — mutualFundSelectionTemplate / …FundListItemTemplate /
                  //  …NoDescriptionLabel / …Scope* / …CandidateLimit — are gone from the palette on purpose:
                  //  a NEW node has no reason to carry them. applyNodeDefaults still keeps them alive on an
                  //  older flow so a re-save cannot strip them.)
                  systemPrompt: '',
                  mutualFundExtractionTemplate: ''
                }
            },
            {
              type: 'apiknowledge',
              name: 'API Knowledge',
              description: 'Fetch knowledge from external API',
              icon: 'mdi-rocket-launch',
              iconBg: '#ffecdb',
              iconColor: '#ff6c37',
              data: { label: 'Knowledge API', type: 'apiknowledge' }
            },
            {
              type: 'products',
              name: 'Products',
              description: 'Product information and catalog',
              icon: 'mdi-package-variant',
              iconBg: '#fef3c7',
              iconColor: '#d97706',
              data: { label: 'Products', type: 'products' }
            },
            {
              type: 'sharepoint',
              name: 'SharePoint',
              description: 'Connect to SharePoint and get files',
              icon: 'mdi-microsoft-sharepoint',
              iconBg: '#e6f7f8',
              iconColor: '#02787c',
              data: { label: 'SharePoint', type: 'sharepoint' }
            },
            // Appended last, so the existing Knowledge entries keep their order for anyone used to
            // the panel. (quickNodes indexes nodeCategories[0..3] only — Knowledge is [4] — so this
            // category is not index-referenced; the index-sensitive one is [3] Agent Architect.)
            {
              type: 'metaDocuments',
              name: 'Meta Documents',
              description: 'Meta Document knowledge by header/category',
              icon: 'mdi-bookshelf',
              iconBg: '#eef2ff',
              iconColor: '#6366f1',
              data: { label: 'Meta Documents', type: 'metaDocuments', metaDocSelections: [], topNChunks: 5, metaDocDescription: '' }
            }
          ]
        },
        {
          name: 'Actions',
          icon: 'mdi-lightning-bolt',
          nodes: [
            {
              type: 'email',
              name: 'Email',
              description: 'Send an email to user',
              icon: 'mdi-email',
              iconBg: '#fef2f2',
              iconColor: '#dc2626',
              data: { label: 'Email', type: 'email' }
            }
          ]
        }
      ]
    }
  },
  computed: {
    filteredCategories() {
      // First apply type exclusion filter
      let categories = this.nodeCategories;
      
      if (this.excludeTypes && this.excludeTypes.length > 0) {
        categories = categories.map(category => ({
          ...category,
          nodes: category.nodes.filter(node => !this.excludeTypes.includes(node.type))
        })).filter(category => category.nodes.length > 0);
      }
      
      // Then apply search filter
      if (!this.searchTerm) {
        return categories;
      }

      return categories.map(category => ({
        ...category,
        nodes: category.nodes.filter(node =>
          node.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          node.description.toLowerCase().includes(this.searchTerm.toLowerCase())
        )
      })).filter(category => category.nodes.length > 0);
    },
    quickNodes() {
      // Most commonly used nodes for collapsed state
      const allQuickNodes = [
        this.nodeCategories[0].nodes[0], // Message Input
        this.nodeCategories[0].nodes[1], // Response
        this.nodeCategories[1].nodes[0], // AI Model
        this.nodeCategories[2].nodes[0], // Intent Handler
        this.nodeCategories[2].nodes[1], // Smart Intent
        this.nodeCategories[1].nodes[1], // Intent AI Model
        this.nodeCategories[3].nodes[0], // Documents
        this.nodeCategories[3].nodes[1], // Knowledge API
      ];
      
      // Apply excludeTypes filter
      if (this.excludeTypes && this.excludeTypes.length > 0) {
        return allQuickNodes.filter(node => node && !this.excludeTypes.includes(node.type));
      }
      
      return allQuickNodes;
    }
  },
  methods: {
    togglePanel() {
      this.panelCollapsed = !this.panelCollapsed;
    },
    onDragStart(event, node) {
      // Store node data for drop handling
      event.dataTransfer.setData('application/reactflow', JSON.stringify(node));
      event.dataTransfer.effectAllowed = 'move';
    },
    addNode(nodeTemplate) {
      // Generate unique ID
      const nodeId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const newNode = {
        id: nodeId,
        type: 'flow-node',
        position: { x: 100, y: 100 }, // Default position, will be updated
        data: { ...nodeTemplate.data }
      };

      this.$emit('add-node', newNode);
    }
  }
}
</script>


<style scoped>
.floating-node-panel {
  position: absolute;
  top: 120px;
  left: 20px;
  z-index: 1000;
  width: 320px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: calc(100vh - 100px);
  height: 85%;
}

.floating-node-panel.collapsed {
  width: 70px;
  height: auto;
  min-height: 400px;
  max-height: calc(100vh - 100px);
}

.panel-card {
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 16px 20px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border-radius: 16px 16px 0 0 !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: auto;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.toggle-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px !important;
  max-height: calc(100vh - 120px);
}

.search-container {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.categories {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.category-panels {
  background: transparent;
}

.category-header {
  padding: 12px !important;
}

.category-title-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.category-icon {
  color: #6b7280;
}

.category-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  flex: 1;
}

.node-count {
  background: #f3f4f6;
  color: #6b7280;
}

.category-content {
  padding: 0 !important;
}

.category-nodes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px 16px;
}

.draggable-node {
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: grab;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}

.draggable-node:hover {
  background: #e8f3ff;
  border-color: #b5cae9;
  transform: translateY(-2px);
}

.draggable-node:active {
  cursor: grabbing;
  transform: translateY(0);
}

.node-preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.node-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.node-info {
  flex: 1;
  min-width: 0;
}

.node-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}

.node-description {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.3;
}

/* Collapsed state */
.collapsed-content {
  padding: 16px 8px !important;
  /* à¹à¸à¹‰à¹„à¸‚à¹ƒà¸™ collapsed state */
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 120px);
}

.quick-nodes {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.quick-node {
  width: 44px;
  height: 44px;
  border: 1px solid #dddee2;
  border-radius: 12px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.quick-node:hover {
  background: #e3f1ff;
  border-color: #bad6ff;
  transform: translateY(-2px);
}

.quick-node:active {
  cursor: grabbing;
  transform: translateY(0);
}

/* Scrollbar styling */
.panel-content::-webkit-scrollbar,
.collapsed-content::-webkit-scrollbar {
  width: 6px;
}

.panel-content::-webkit-scrollbar-track,
.collapsed-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb,
.collapsed-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb:hover,
.collapsed-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Responsive */
@media (max-width: 768px) {
  .floating-node-panel {
    width: 280px;
    top: 10px;
    left: 10px;
    max-height: calc(100vh - 20px);
  }

  .floating-node-panel.collapsed {
    width: 60px;
  }

  .panel-content {
    max-height: calc(100vh - 100px);
  }

  .collapsed-content {
    max-height: calc(100vh - 100px);
  }

  .draggable-node {
    padding: 10px;
  }

  .node-name {
    font-size: 12px;
  }

  .node-description {
    font-size: 10px;
  }
}

/* Glass effect enhancement */
@supports (backdrop-filter: blur(10px)) {
  .panel-card {
    background: rgba(255, 255, 255, 0.85) !important;
  }
}

/* Enhanced Toggle Button with Hint Animation */
.collapse-btn {
  position: relative;
  overflow: visible;
  background: transparent !important;
  color: rgb(var(--v-theme-primary)) !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  min-width: 120px;
  height: 36px;
}

.animated-hint-btn:hover {
  transform: translateY(-2px);
  filter: brightness(0.9);
  animation-play-state: paused;
}

.animated-hint-btn.collapsed-hint {
  min-width: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50% !important;
  background: rgb(var(--v-theme-primary)) !important;
  /* box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.4) !important; */
  /* animation: hint-bounce-collapsed 6s infinite; */
}

.animated-hint-btn.collapsed-hint:hover {
  filter: brightness(0.9);
  /* box-shadow: 0 8px 24px rgba(var(--v-theme-primary), 0.6) !important; */
  animation-play-state: paused;
}

/* Bounce Animations */
@keyframes hint-bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  10% {
    transform: translateY(-8px) scale(1.05);
  }

  30% {
    transform: translateY(-4px) scale(1.02);
  }

  40% {
    transform: translateY(-2px) scale(1.01);
  }

  60% {
    transform: translateY(-6px) scale(1.03);
  }

  70% {
    transform: translateY(-2px) scale(1.01);
  }
}

@keyframes hint-bounce-collapsed {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0) scale(1);
  }

  10% {
    transform: translateY(-6px) scale(1.1);
  }

  30% {
    transform: translateY(-3px) scale(1.05);
  }

  40% {
    transform: translateY(-1px) scale(1.02);
  }

  60% {
    transform: translateY(-5px) scale(1.08);
  }

  70% {
    transform: translateY(-2px) scale(1.03);
  }
}

.btn-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 2;
}

.original-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.collapse-text {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.toggle-icon {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hint Animation Circles */
.hint-circle {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  pointer-events: none;
  opacity: 0;
}

.hint-circle-1 {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 60px;
  height: 60px;
  animation: hint-pulse 2s infinite;
  animation-delay: 0s;
}

.hint-circle-2 {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80px;
  height: 80px;
  animation: hint-pulse 2s infinite;
  animation-delay: 0.3s;
}

.hint-circle-3 {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100px;
  height: 100px;
  animation: hint-pulse 2s infinite;
  animation-delay: 0.6s;
}

/* Collapsed state hint circles */
.collapsed-hint .hint-circle-1 {
  width: 50px;
  height: 50px;
  border-color: rgba(255, 255, 255, 0.8);
}

.collapsed-hint .hint-circle-2 {
  width: 70px;
  height: 70px;
  border-color: rgba(255, 255, 255, 0.6);
}

.collapsed-hint .hint-circle-3 {
  width: 90px;
  height: 90px;
  border-color: rgba(255, 255, 255, 0.4);
}

/* Enhanced Keyframe animations */
@keyframes hint-pulse {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.3);
  }

  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.4);
  }
}

/* Text transition animation */
.collapse-text-enter-active,
.collapse-text-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.collapse-text-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.collapse-text-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

/* Hover effects - Stop all animations */
.animated-hint-btn:hover .hint-circle {
  animation-play-state: paused;
  opacity: 0 !important;
}

/* Focus and active states */
.animated-hint-btn:focus .hint-circle,
.animated-hint-btn:active .hint-circle {
  animation-play-state: paused;
  opacity: 0 !important;
}

.animated-hint-btn:focus,
.animated-hint-btn:active {
  animation-play-state: paused;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .hint-circle-1 {
    width: 50px;
    height: 50px;
  }

  .hint-circle-2 {
    width: 70px;
    height: 70px;
  }

  .hint-circle-3 {
    width: 90px;
    height: 90px;
  }

  .collapsed-hint .hint-circle-1 {
    width: 45px;
    height: 45px;
  }

  .collapsed-hint .hint-circle-2 {
    width: 65px;
    height: 65px;
  }

  .collapsed-hint .hint-circle-3 {
    width: 85px;
    height: 85px;
  }

  /* Smaller bounce for mobile */
  @keyframes hint-bounce {

    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }

    10% {
      transform: translateY(-4px) scale(1.03);
    }

    30% {
      transform: translateY(-2px) scale(1.01);
    }

    60% {
      transform: translateY(-3px) scale(1.02);
    }

    70% {
      transform: translateY(-1px) scale(1.01);
    }
  }
}

/* Accessibility - Respect prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .animated-hint-btn {
    animation: none;
  }

  .hint-circle {
    animation: none;
    opacity: 0;
  }
}
</style>
